import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../styles/ag-grid-custom.css";
import { Search, ArrowLeft, Trash2 } from "lucide-react";
import images from "../../assets/images";
import { ColDef, ICellRendererParams, GridReadyEvent } from "ag-grid-community";
import { useFireSafetyAudits } from "../../store/store";
import { CheckListItem } from "../../types/api";
import { isDefined } from "../../common/utils";

const CheckList: React.FC = () => {
  const [rowData, setRowData] = useState<CheckListItem[]>();
  const [loading, setLoading] = useState<boolean>(false);
  const [moduleName, setModuleName] = useState<string>("");
  const location = useLocation();
  const {
    allChickList,
    deleteChickList,
    searchCheckList,
    getCheckList,
    getAllChecklist,
  } = useFireSafetyAudits();
  const getModuleName = (): string | undefined => {
    const path = location.pathname;
    if (path.includes("/hazardous-materials")) {
      return "hazardous_materials";
    } else if (
      path.includes("/fire-system") ||
      path.startsWith("/fire-safety-audits/check-list")
    ) {
      return "fire_system";
    } else if (
      path.includes(
        "/equipment-management/maintenance-scheduling-and-tracking/check-list",
      )
    ) {
      return "equipment_management";
    }
    return undefined;
  };
  const myModuleName = getModuleName() || "";
  useEffect(() => {
    setModuleName(myModuleName);
    if (myModuleName) {
      getAllChecklist(myModuleName);
    }
  }, [myModuleName, getAllChecklist]);
  useEffect(() => {
    const chickListData = allChickList.map((item: CheckListItem) => {
      return {
        _id: item._id,
        checkList: item.checkList,
        itemNo: item.itemNo,
        riskRating: item.riskRating,
        appliesToArea: item.appliesToArea,
        auditType: item.auditType,
        isActive: item.isActive,
        moduleName: item.moduleName,
      };
    });
    setRowData(chickListData);
  }, [allChickList]);
  const navigate = useNavigate();
  const handleBack = () => {
    if (moduleName === "hazardous_materials") {
      navigate("/hazardous-materials/audits/perform-audits");
    } else if (moduleName === "equipment_management") {
      navigate("/equipment-management/maintenance-scheduling-and-tracking");
    } else {
      navigate("/fires-safety-audits");
    }
  };

  const handleCreateCheckList = () => {
    if (moduleName === "hazardous_materials") {
      navigate("/hazardous-materials/audits/create-check-list");
    } else if (moduleName === "fire_system") {
      navigate("/fire-safety-audits/create-check-list");
    } else if (moduleName == "equipment_management") {
      navigate(
        "/equipment-management/maintenance-scheduling-and-tracking/create-check-list",
      );
    } else {
      navigate("/dashboard");
    }
  };
  const activeStatusCellRenderer = (params: ICellRendererParams) => {
    const isActive = params.value;

    return (
      <div
        className={`font-medium mt-2 ${
          isActive ? "text-teal-500" : "text-red-500"
        }`}
      >
        {isActive ? "Active" : "Inactive"}
      </div>
    );
  };

  // Action cell renderer
  const actionCellRenderer = (params: ICellRendererParams) => {
    return (
      <div className="flex gap-2 mt-2 items-center">
        <Link
          to={
            params.data.moduleName === "hazardous_materials"
              ? "/hazardous-materials/audits/view-check-list"
              : params.data.moduleName === "equipment_management"
                ? "/equipment-management/maintenance-scheduling-and-tracking/view-check-list"
                : "/fire-safety-audits/view-check-list"
          }
          state={{
            mode: "view",
            id: params.data._id,
            moduleName: params.data.moduleName,
          }}
          className="focus:outline-none transition-transform"
          onClick={() => getCheckList(params.data._id, moduleName)}
          title="View"
        >
          <img className="iconTd" src={images.viewIcon} alt="View" />
        </Link>
        <Link
          to={
            params.data.moduleName === "hazardous_materials"
              ? "/hazardous-materials/audits/create-check-list"
              : params.data.moduleName === "equipment_management"
                ? "/equipment-management/maintenance-scheduling-and-tracking/create-check-list"
                : "/fire-safety-audits/create-check-list"
          }
          state={{ mode: "edit", id: params.data._id }}
          className="focus:outline-none transition-transform"
          onClick={() => getCheckList(params.data._id, moduleName)}
          title="Edit"
        >
          <img className="iconTd" src={images.editIcon} alt="Edit" />
        </Link>
        <button
          className="focus:outline-none transition-transform"
          onClick={() => deleteChickList(params.data._id, moduleName)}
          title="Delete"
        >
          <Trash2 className="text-red-500" size={25} />
        </button>
      </div>
    );
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Checklist Name",
        field: "checkList",
        flex: 1,
      },
      {
        headerName: "Item No",
        field: "itemNo",
        flex: 1,
      },
      {
        headerName: "Risk Rating",
        field: "riskRating",
        flex: 1,
      },
      {
        headerName: "Applies To Area",
        field: "appliesToArea",
        flex: 1,
      },
      {
        headerName: "Audit Type",
        field: "auditType",
        flex: 1,
      },
      {
        headerName: "Is Active",
        field: "isActive",
        flex: 1,
        cellRenderer: activeStatusCellRenderer,
      },
      {
        headerName: "Action",
        field: "actions",
        flex: 1,
        cellRenderer: actionCellRenderer,
      },
    ],
    [],
  );

  // Handle grid ready event to auto-size columns
  const onGridReady = (params: GridReadyEvent) => {
    params.api.sizeColumnsToFit();
  };

  return (
    <div className="px-2 py-4 mt-14 w-full h-full">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">
          Audit Check List
        </h2>
        <button
          onClick={handleBack}
          className="flex items-center gap-2 bg-[#61748e]  text-white px-4 py-2 rounded-full transition-colors"
        >
          <ArrowLeft size={20} />
          BACK
        </button>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 w-full">
        {/* Search and Action Bar */}
        <div className="flex flex-wrap justify-between items-center gap-4 p-4 border-b border-gray-200">
          {/* Search Bar */}
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-500" />
            </div>
            <input
              type="text"
              placeholder="Search"
              onChange={(e) => {
                const value = e.target.value;
                if (isDefined(value)) {
                  searchCheckList(value, moduleName);
                }
              }}
              className="block w-full pl-10 pr-3 py-2 border-2 border-[#a5b7cf] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Action Button */}
          <div className="flex gap-3">
            <button
              onClick={handleCreateCheckList}
              className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
            >
              <img src={images.tasksIcon} alt="" width={25} />
              CREATE CHECK LIST
            </button>
          </div>
        </div>

        {/* AG Grid Table */}
        <div
          className="ag-theme-alpine w-full"
          style={{
            height: "calc(100vh - 350px)",
            width: "100%",
          }}
        >
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <div className="text-gray-500">Loading check list items...</div>
            </div>
          ) : (
            <AgGridReact
              rowData={rowData}
              columnDefs={columnDefs}
              rowHeight={60}
              headerHeight={60}
              onGridReady={onGridReady}
              suppressCellFocus={true}
              animateRows={true}
              rowSelection="single"
              suppressRowClickSelection={true}
              domLayout="normal"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckList;
